import { BoundTreeSelectField, NestedOption } from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { InputMaybe, PlanScheduleType, PlanScheduleTypeDetail } from "src/generated/graphqlTypes";

// "All Structures" groups the two structure types in the picker and is never saved. We cast as a PlanScheduleType so
// the tree and the form field share one simplified value type -> onSelect saves only the leaf/true PlanScheduleType values.
const allStructuresGroup = { code: "ALL_STRUCTURES" as PlanScheduleType, name: "All Structures" };
const structureTypes = [PlanScheduleType.StructureMain, PlanScheduleType.StructureAuxiliary];

/** New catalog tasks apply to all structures. Mirrors the `GlobalPlanTask.appliesTo` default on the backend. */
export const defaultAppliesTo = structureTypes;

type AppliesToFieldProps = {
  field: FieldState<InputMaybe<PlanScheduleType[]>>;
  planScheduleTypes: PlanScheduleTypeDetail[];
};

/** Picks which kinds of plan schedules a catalog task applies to. Saves only the leaf schedule types. */
export function AppliesToField(props: AppliesToFieldProps) {
  const { field, planScheduleTypes } = props;
  return (
    <BoundTreeSelectField
      label="Applies to"
      tooltip={`Which kinds of project schedules this task is automatically added to when "Automatically include this task" is enabled.`}
      field={field}
      options={getAppliesToOptions(planScheduleTypes)}
      getOptionValue={(o) => o.code}
      getOptionLabel={(o) => o.name}
      // Save only the leaves, so the "All Structures" group itself is never saved
      onSelect={({ leaf }) => field.set(leaf.values)}
      chipDisplay="root"
    />
  );
}

/** Groups the two (default) Structure types under "All Structures" in the picker. */
function getAppliesToOptions(planScheduleTypes: PlanScheduleTypeDetail[]): NestedOption<PlanScheduleTypeDetail>[] {
  const [structures, others] = planScheduleTypes.partition((t) => structureTypes.includes(t.code));
  return [{ ...allStructuresGroup, children: structures }, ...others].sortByKey("name");
}
